import { isOpeningClass, isRoomClass, isWallClass } from './roboflowService';
import { roomsFromWalls, labelRooms } from './roomsFromWalls';

// ── FLOOR PLAN → 2D VECTORS ──────────────────────────────────────────────────
// Turns an uploaded floor plan into the 2D vector geometry the editor works
// in: wall lines, door markers, window markers and room regions.
//
// NOTHING HERE IS 3D. The import used to build Three.js meshes directly, which
// meant the user was shown a result they could look at but not correct. Going
// through 2D vectors instead puts every detected element into the ordinary 2D
// editor as a native, editable shape — draggable, resizable, deletable — and
// the existing Builder → 3D pipeline then renders the scene exactly as it does
// for a hand-drawn plan. One 3D renderer, not two.
//
// ══ COORDINATE SYSTEM ═══════════════════════════════════════════════════════
//
// Roboflow works in IMAGE pixels: origin top-left, +x right, +y DOWN, and each
// box is given by its CENTRE plus a width/height.
//
// Three.js here works in METRES on the X/Z floor plane with the plan CENTRED on
// the origin, matching the rest of the app (House3DView: 1 grid cell = 1 metre,
// +z is "down" the plan). The mapping is therefore:
//
//     worldX = (pxX - imageWidth  / 2) * scaleFactor
//     worldZ = (pxY - imageHeight / 2) * scaleFactor
//
// `scaleFactor` is METRES PER PIXEL — the one number that calibrates a drawing
// of unknown scale to real dimensions. Wall HEIGHT is deliberately not affected
// by it: re-calibrating a plan changes how wide the rooms are, not how tall the
// ceiling is.
//
// ══ WHERE WALLS COME FROM ═══════════════════════════════════════════════════
//
// The configured model has no `wall` class (see roboflowService), so walls are
// taken from the best source available, in this order:
//
//   1. `wall` detections, if the model ever gains that class.
//   2. THE DRAWING ITSELF — wallExtract reads the solid black bands a floor
//      plan uses for its walls. This is the good path: it recovers the real
//      interior walls, in their real positions, including every wall the
//      detector never had a class for.
//   3. The perimeters of the detected room boxes, merged where they coincide.
//
// (3) was the original approach and it does not work on its own. A room box is
// a loose rectangle around a labelled region, so neighbouring rooms do not
// share an edge; the output is a handful of disconnected boxes floating apart
// with gaps where the party walls should be. It survives only as a fallback for
// drawings too faint for (2) to read, where a rough shape beats nothing.

export const DEFAULT_WALL_HEIGHT = 2.8;    // metres — matches houseSlice.wallHeight
export const WALL_THICKNESS      = 0.15;   // matches House3DView's WT

// Opening dimensions mirror the app's own OPENING_TYPE_DEFAULTS so an imported
// plan and a hand-drawn one produce doors and windows of the same size.
export const DOOR_HEIGHT   = 2.06;
export const WINDOW_HEIGHT = 1.20;
export const WINDOW_SILL   = 0.90;    // metres off the floor

// Two rooms whose boxes overlap this much are the same room detected twice.
const ROOM_IOU_LIMIT = 0.35;
// How far an opening may sit from a wall and still be considered on it.
const SNAP_TOLERANCE = 0.9;           // metres
// Below this many extracted walls we assume the drawing was unreadable (a
// shaded or low-contrast plan) and fall back to the room boxes.
const MIN_IMAGE_WALLS = 4;
// How far a room edge may be dragged to meet a wall line.
const SNAP_ROOM_REACH = 1.2;         // metres
// Collinear edges closer than this are the same wall.
const MERGE_TOLERANCE = 0.45;         // metres

// ── helpers ──────────────────────────────────────────────────────────────────

/** Roboflow centre+size box → pixel-space edges. */
function toPixelRect(p) {
  return {
    x0: p.x - p.width / 2,
    y0: p.y - p.height / 2,
    x1: p.x + p.width / 2,
    y1: p.y + p.height / 2,
  };
}

function iou(a, b) {
  const ix = Math.max(0, Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0));
  const iy = Math.max(0, Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0));
  const inter = ix * iy;
  if (!inter) return 0;
  const areaA = (a.x1 - a.x0) * (a.y1 - a.y0);
  const areaB = (b.x1 - b.x0) * (b.y1 - b.y0);
  return inter / (areaA + areaB - inter);
}

/**
 * Drops duplicate detections of the same room.
 *
 * Detections arrive unordered; taking the most confident first means a
 * near-duplicate is measured against the better box, not an arbitrary one.
 */
function dedupeRooms(rects) {
  const kept = [];
  for (const r of [...rects].sort((a, b) => b.confidence - a.confidence)) {
    if (kept.some(k => iou(k, r) > ROOM_IOU_LIMIT)) continue;
    kept.push(r);
  }
  return kept;
}

/**
 * Merges collinear, overlapping wall edges into one.
 *
 * Adjacent rooms each contribute an edge along the wall they share. Left alone
 * that is two boxes in the same place — visible as z-fighting, and it doubles
 * the geometry. Segments are bucketed by their fixed axis (rounded to
 * MERGE_TOLERANCE) and their spans unioned.
 */
function mergeSegments(segments) {
  const buckets = new Map();
  for (const s of segments) {
    const key = `${s.axis}:${Math.round(s.at / MERGE_TOLERANCE)}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(s);
  }

  const out = [];
  for (const group of buckets.values()) {
    group.sort((a, b) => a.from - b.from);
    // Average the fixed axis across the bucket, so a merged wall sits between
    // the two rooms rather than snapping to whichever edge came first.
    let cur = { ...group[0], _n: 1, _sum: group[0].at };
    for (let i = 1; i < group.length; i++) {
      const s = group[i];
      if (s.from <= cur.to + MERGE_TOLERANCE) {
        cur.to = Math.max(cur.to, s.to);
        cur._sum += s.at;
        cur._n += 1;
      } else {
        cur.at = cur._sum / cur._n;
        out.push(cur);
        cur = { ...s, _n: 1, _sum: s.at };
      }
    }
    cur.at = cur._sum / cur._n;
    out.push(cur);
  }
  return out.map(({ _n, _sum, ...s }) => s);
}

/**
 * Snaps room boxes onto the wall lines read off the drawing.
 *
 * This is what makes an imported plan a HOME rather than a pile of boxes. A
 * detector's room box is a loose rectangle: two rooms either side of a wall
 * come back with a gap or an overlap between them, never a shared edge. Snapping
 * each edge to the nearest real wall line pulls neighbouring rooms onto the SAME
 * line, so they meet exactly along the wall that separates them.
 *
 * Edges with no wall line nearby are left alone — better a slightly loose room
 * than one dragged across the plan to a wall it has nothing to do with.
 *
 * @param rooms  world-metre rects {x0,x1,z0,z1}
 * @param segs   world-metre wall segments
 * @param reach  how far an edge may be moved, in metres
 */
function snapRoomsToWalls(rooms, segs, reach) {
  const xLines = [...new Set(segs.filter(s => s.axis === 'v').map(s => s.at))].sort((a, b) => a - b);
  const zLines = [...new Set(segs.filter(s => s.axis === 'h').map(s => s.at))].sort((a, b) => a - b);
  if (!xLines.length && !zLines.length) return rooms;

  const snap = (v, lines) => {
    let best = v, bestD = reach;
    for (const l of lines) {
      const d = Math.abs(l - v);
      if (d < bestD) { bestD = d; best = l; }
    }
    return best;
  };

  return rooms.map(r => {
    const x0 = snap(Math.min(r.x0, r.x1), xLines);
    const x1 = snap(Math.max(r.x0, r.x1), xLines);
    const z0 = snap(Math.min(r.z0, r.z1), zLines);
    const z1 = snap(Math.max(r.z0, r.z1), zLines);
    // A room collapsed by snapping (both edges onto one line) keeps its original
    // extent on that axis rather than becoming a zero-width slab.
    return {
      ...r,
      x0: x1 - x0 > 0.5 ? x0 : Math.min(r.x0, r.x1),
      x1: x1 - x0 > 0.5 ? x1 : Math.max(r.x0, r.x1),
      z0: z1 - z0 > 0.5 ? z0 : Math.min(r.z0, r.z1),
      z1: z1 - z0 > 0.5 ? z1 : Math.max(r.z0, r.z1),
    };
  });
}

/** The four perimeter edges of a room rectangle, in world metres. */
function roomEdges(r) {
  return [
    { axis: 'h', at: r.z0, from: r.x0, to: r.x1 },   // top
    { axis: 'h', at: r.z1, from: r.x0, to: r.x1 },   // bottom
    { axis: 'v', at: r.x0, from: r.z0, to: r.z1 },   // left
    { axis: 'v', at: r.x1, from: r.z0, to: r.z1 },   // right
  ];
}

/**
 * How wide the opening should actually be, in metres.
 *
 * A DOOR's bounding box is not the door: this model is trained on plans drawn
 * with a swing arc, so the box encloses the arc too and comes back roughly
 * square and about twice the true leaf width. Its size is therefore a weak
 * signal and the result is clamped to a believable door width. A WINDOW is
 * drawn as a plain run in the wall, so its box IS the window and is trusted
 * over a much wider range.
 */
function openingSpan(type, boxSpan) {
  return type === 'door'
    ? Math.min(1.1, Math.max(0.7, boxSpan * 0.55))
    : Math.min(3.0, Math.max(0.5, boxSpan));
}

/**
 * Attaches each opening to the wall it belongs to, and reports the ones that
 * matched nothing.
 *
 * The tolerance GROWS WITH THE DETECTION, which matters for exactly the reason
 * above: a door box inflated by its swing arc has its centre pushed up to half
 * a box-width off the wall it belongs to, so a fixed tolerance drops precisely
 * the doors that were detected most clearly.
 */
/**
 * Splits walls into the outer shell and everything inside it.
 *
 * A wall lying on the overall bounding box is part of the perimeter. This
 * matters because windows are almost always in an EXTERIOR wall — a window onto
 * a corridor is the exception — so knowing which walls face outside is a strong
 * prior for placing them.
 */
function classifyWalls(segments) {
  if (!segments.length) return { perimeter: [], interior: [] };
  const xs = segments.flatMap(s => (s.axis === 'v' ? [s.at] : [s.from, s.to]));
  const zs = segments.flatMap(s => (s.axis === 'h' ? [s.at] : [s.from, s.to]));
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minZ = Math.min(...zs), maxZ = Math.max(...zs);
  const edge = 0.6;                       // metres of slack for a wall's own thickness

  const perimeter = [], interior = [];
  for (const s of segments) {
    const onEdge = s.axis === 'h'
      ? (Math.abs(s.at - minZ) < edge || Math.abs(s.at - maxZ) < edge)
      : (Math.abs(s.at - minX) < edge || Math.abs(s.at - maxX) < edge);
    (onEdge ? perimeter : interior).push(s);
  }
  return { perimeter, interior };
}

/**
 * Puts every opening on a wall.
 *
 * ══ THE RULE: NOTHING IS EVER DROPPED, NOTHING EVER FLOATS ══════════════════
 *
 * Two earlier versions each failed one half of that. The first placed an
 * opening on the nearest wall within a fixed radius and invented a stub wall
 * when nothing matched — so doors ended up floating in mid-air, attached to
 * nothing. The second demanded the wall pass through the detection box and
 * dropped whatever failed — which threw away nearly every door and window and
 * produced an empty shell.
 *
 * So: the best wall is CHOSEN by score, but some wall is always chosen, and the
 * opening is then clamped onto it. A door on a slightly wrong wall is a small
 * error the user can drag; a missing door is a hole in the model, and a
 * floating one is a bug.
 *
 * Scoring is perpendicular distance, discounted when the wall actually passes
 * through the detection box (a door is drawn IN its wall) and, for windows,
 * when the wall is part of the outer shell.
 */
function assignOpenings(openings, segments) {
  const unplaced = [];
  if (!segments.length) return openings.slice();

  const { perimeter } = classifyWalls(segments);
  const isPerimeter = new Set(perimeter);

  for (const o of openings) {
    let best = null, bestScore = Infinity;

    for (const seg of segments) {
      const along = seg.axis === 'h' ? o.x : o.z;
      const off   = seg.axis === 'h' ? o.z : o.x;

      // How far outside the wall's span the opening sits (0 when within it).
      const outside = Math.max(0, seg.from - along, along - seg.to);
      const perpHalf = (seg.axis === 'h' ? o.height : o.width) / 2;
      const dist = Math.abs(off - seg.at);

      let score = dist + outside * 2;      // straying past the end is worse
      if (dist <= perpHalf) score *= 0.45; // the wall runs through the box
      if (o.type === 'window' && isPerimeter.has(seg)) score *= 0.6;

      if (score < bestScore) { bestScore = score; best = { seg, along }; }
    }
    if (!best) { unplaced.push(o); continue; }

    // Span measured along the wall's own direction — an opening on a vertical
    // wall runs along the box's HEIGHT, not its width.
    const boxSpan = best.seg.axis === 'h' ? o.width : o.height;
    const span = openingSpan(o.type, boxSpan);
    const wallLen = best.seg.to - best.seg.from;
    if (wallLen < 0.4) { unplaced.push(o); continue; }   // wall too short to hold anything

    // Clamp the CENTRE so the whole opening stays inside the wall, rather than
    // clipping its ends — that is what keeps it seated instead of hanging off.
    const half = Math.min(span, wallLen * 0.9) / 2;
    const centre = Math.min(best.seg.to - half, Math.max(best.seg.from + half, best.along));

    (best.seg.openings = best.seg.openings || []).push({
      type: o.type, from: centre - half, to: centre + half,
    });
  }
  return unplaced;
}

/**
 * Builds a short wall for an opening that matched none.
 *
 * A detected door is evidence that a wall exists there, even when no room was
 * detected around it — and with no `wall` class in the model, sparse room
 * detections otherwise throw away perfectly good doors. A stub is better than
 * a dropped opening: the user sees the door where the plan shows one, standing
 * in a fragment of wall they can extend.
 *
 * Orientation follows the majority of nearby walls when there are any (plans
 * are overwhelmingly rectilinear); failing that, the box's own aspect.
 */
function stubSegmentsFor(openings, segments) {
  const stubs = [];
  for (const o of openings) {
    let axis;
    const near = segments
      .map(s => ({ s, d: Math.abs((s.axis === 'h' ? o.z : o.x) - s.at) }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 3);
    if (near.length) axis = near[0].s.axis;
    else axis = o.width >= o.height ? 'h' : 'v';

    const along = axis === 'h' ? o.x : o.z;
    const at    = axis === 'h' ? o.z : o.x;
    const half  = openingSpan(o.type, axis === 'h' ? o.width : o.height) / 2;
    // Extended past the opening so it reads as a piece of wall containing a
    // door, not a door with two slivers glued to its sides.
    const pad = 0.6;
    const seg = { axis, at, from: along - half - pad, to: along + half + pad, _stub: true };
    seg.openings = [{ type: o.type, from: along - half, to: along + half }];
    stubs.push(seg);
  }
  return stubs;
}

// ── public API ───────────────────────────────────────────────────────────────

/**
 * Turns raw predictions into plan geometry, in METRES, centred on the origin.
 *
 * Pure data — no Three.js objects. Both consumers run through here so the 3D
 * preview and the editable rooms written into the project can never disagree
 * about where a wall is.
 *
 * @param {{image:{width,height}, predictions:Array}} detection
 * @param {object} [opts]
 * @param {number} [opts.scaleFactor]  METRES PER PIXEL
 * @param {Array}  [opts.imageWalls]   wall segments read off the drawing by
 *                                     wallExtract, in IMAGE PIXELS
 * @returns {{rooms:Array, segments:Array, openings:Array, stats:object}}
 */
export function analysePlan(detection, opts = {}) {
  const { scaleFactor = 0.02, imageWalls = null } = opts;

  const image = detection?.image || { width: 0, height: 0 };
  const preds = detection?.predictions || [];
  const cx = (image.width || 0) / 2;
  const cy = (image.height || 0) / 2;
  const toWorldX = px => (px - cx) * scaleFactor;
  const toWorldZ = py => (py - cy) * scaleFactor;

  // ── rooms → world rectangles ──
  let roomRects = dedupeRooms(
    preds.filter(p => isRoomClass(p.class)).map(p => ({ ...toPixelRect(p), confidence: p.confidence || 0, cls: p.class })),
  ).map(r => ({
    cls: r.cls,
    confidence: r.confidence,
    x0: toWorldX(r.x0), x1: toWorldX(r.x1),
    z0: toWorldZ(r.y0), z1: toWorldZ(r.y1),
  }));

  // ── walls, from the best source available ────────────────────────────────
  //
  //   1. a `wall` class, if the model ever gains one — authoritative
  //   2. the DRAWING itself (wallExtract), which is what a plan actually
  //      encodes: real interior walls, in the real places
  //   3. the room bounding boxes — the last resort
  //
  // (3) alone cannot reconstruct a plan: room boxes do not share edges, so
  // adjacent rooms come out as separate boxes with gaps between them instead of
  // a connected home. It is kept only for plans whose walls are too faint to
  // read, where a rough shape beats nothing.
  const detectedWalls = preds.filter(p => isWallClass(p.class));
  let segments;
  let wallSource;
  if (!detectedWalls.length && imageWalls && imageWalls.length >= MIN_IMAGE_WALLS) {
    wallSource = 'image';
    segments = imageWalls.map(s => {
      const along = s.axis === 'h' ? toWorldX : toWorldZ;
      const seg = s.axis === 'h'
        ? { axis: 'h', at: toWorldZ(s.at), from: toWorldX(s.from), to: toWorldX(s.to) }
        : { axis: 'v', at: toWorldX(s.at), from: toWorldZ(s.from), to: toWorldZ(s.to) };
      // Openings read straight off the DRAWING: the gap in the wall band IS the
      // door or window, so its wall, position and width are exact by
      // construction. Nothing to match, nothing to guess.
      if (s.openings?.length) {
        seg.openings = s.openings.map(o => ({ type: o.type, from: along(o.from), to: along(o.to) }));
      }
      return seg;
    });
  } else if (detectedWalls.length) {
    wallSource = 'detected';
    segments = detectedWalls.map(p => {
      const r = toPixelRect(p);
      const x0 = toWorldX(r.x0), x1 = toWorldX(r.x1);
      const z0 = toWorldZ(r.y0), z1 = toWorldZ(r.y1);
      // A wall box is long in one axis — that axis is the wall's direction.
      return (x1 - x0) >= (z1 - z0)
        ? { axis: 'h', at: (z0 + z1) / 2, from: x0, to: x1 }
        : { axis: 'v', at: (x0 + x1) / 2, from: z0, to: z1 };
    });
  } else {
    wallSource = 'rooms';
    segments = roomRects.flatMap(roomEdges);
  }
  // Room-derived edges arrive doubled wherever two rooms abut, so they must be
  // merged. Extracted walls are already one-per-wall and merging them would
  // only pull genuinely parallel walls together.
  if (wallSource === 'rooms') segments = mergeSegments(segments);

  // ── openings ──
  const openings = preds.filter(p => isOpeningClass(p.class)).map(p => {
    const r = toPixelRect(p);
    return {
      type: p.class,
      x: toWorldX(p.x),
      z: toWorldZ(p.y),
      width:  Math.abs(toWorldX(r.x1) - toWorldX(r.x0)),
      height: Math.abs(toWorldZ(r.y1) - toWorldZ(r.y0)),
    };
  });
  // ROOMS COME FROM THE WALLS when we have real ones. Every room is then a
  // space actually enclosed by the drawing, which is the only way to get rooms
  // that meet their walls exactly — and the only way to get them at all on the
  // many plans where the detector recognises no room.
  //
  // The detections are still used, but only to NAME the regions.
  if (wallSource === 'image') {
    const enclosed = roomsFromWalls(segments);
    if (enclosed.length) {
      roomRects = labelRooms(enclosed, roomRects).map(r => ({
        cls: r.cls || 'room', confidence: 1,
        x0: r.x0, x1: r.x1, z0: r.z0, z1: r.z1,
        // The region's real cells — a room is often L-shaped, and its bounding
        // box alone would overlap whatever it wraps around.
        parts: r.parts,
      }));
    } else {
      roomRects = snapRoomsToWalls(roomRects, segments, SNAP_ROOM_REACH);
    }
  }

  // Openings found in the drawing are authoritative. The detector's boxes are
  // only consulted for walls that yielded none, so a plan drawn in an unusual
  // style still gets its doors — but a conventional one is never second-guessed.
  const drawn = segments.reduce((n, sg) => n + (sg.openings?.length || 0), 0);

  // POSITION FROM THE DRAWING, TYPE FROM THE DETECTOR.
  //
  // Each source is trusted for what it is actually good at. The gap in the wall
  // band gives an opening's wall, position and width exactly — the detector's
  // box never can. But telling a window from a door by the symbol drawn in the
  // gap is a judgement call on a few pixels, and the model, trained on hundreds
  // of real plans, is simply better at it.
  //
  // So where a detection sits on top of a gap we found, its class wins. Gaps
  // the model said nothing about keep the type inferred from the drawing.
  if (drawn && openings.length) {
    for (const sg of segments) {
      for (const o of sg.openings || []) {
        const cx = sg.axis === 'h' ? (o.from + o.to) / 2 : sg.at;
        const cz = sg.axis === 'h' ? sg.at : (o.from + o.to) / 2;
        let best = null, bestDist = Infinity;
        for (const d of openings) {
          const dist = Math.hypot(d.x - cx, d.z - cz);
          if (dist < bestDist) { bestDist = dist; best = d; }
        }
        // Within a door's width of the gap's centre — beyond that it is a
        // different opening entirely.
        if (best && bestDist < 1.4) o.type = best.type;
      }
    }
  }

  const unplaced = drawn ? [] : assignOpenings(openings, segments);

  // assignOpenings now seats every opening on some wall, so this only fires
  // when there were no walls at all to seat them on — a plan too faint to read
  // and with no rooms detected either. A stub is then the only way to show the
  // user that a door was found there.
  const stubs = stubSegmentsFor(unplaced, segments);
  segments = segments.concat(stubs);

  const placed = segments.reduce((n, s) => n + (s.openings?.length || 0), 0);
  return {
    rooms: roomRects,
    segments,
    openings,
    scaleFactor,
    wallSource,
    stats: {
      wallSource,
      rooms:    roomRects.length,
      walls:    segments.length,
      doors:    drawn
        ? segments.reduce((n, sg) => n + (sg.openings || []).filter(o => o.type === 'door').length, 0)
        : openings.filter(o => o.type === 'door').length,
      windows:  drawn
        ? segments.reduce((n, sg) => n + (sg.openings || []).filter(o => o.type === 'window').length, 0)
        : openings.filter(o => o.type === 'window').length,
      // Openings that needed a wall invented for them. A high count means the
      // model found few rooms, so the derived walls cover little of the plan.
      stubbed:  stubs.length,
      unplaced: openings.length - placed,
    },
  };
}

/**
 * The plan as three flat lists of 2D vectors: walls, doors, windows.
 *
 * ══ THE MAPPING, END TO END ═════════════════════════════════════════════════
 *
 *   Roboflow gives      { x, y, width, height, class }   — image PIXELS, and
 *                                                          x/y is the box CENTRE
 *   wallExtract gives   wall bands + the gaps in them    — image PIXELS
 *   analysePlan gives   segments + rooms                 — METRES, origin-centred
 *   this gives          walls / doors / windows          — METRES, ready to draw
 *
 * The pixel → metre step is a translation and a scale, nothing more:
 *
 *     worldX = (pxX - imageWidth  / 2) * scaleFactor
 *     worldY = (pxY - imageHeight / 2) * scaleFactor
 *
 * `scaleFactor` is metres per pixel — the one number a drawing cannot tell us,
 * which is why the import exposes it as a slider.
 *
 * ══ WHY OPENINGS COME OUT AS THEIR OWN LIST ═════════════════════════════════
 *
 * Internally a door is stored ON its wall, because that is the only way to know
 * which wall it cuts through and to split the wall around it. But an editor
 * wants to draw and hit-test a door as its own shape, so this flattens them out
 * while keeping `wallIndex` — the link back, so moving a wall can carry its
 * openings with it.
 *
 * @param {{rooms:Array, segments:Array}} plan  from analysePlan
 * @returns {{walls:Array, doors:Array, windows:Array, rooms:Array, bounds:object}}
 *   walls   {start:{x,y}, end:{x,y}, thickness, axis}
 *   doors   {center:{x,y}, width, axis, wallIndex}
 *   windows {center:{x,y}, width, axis, wallIndex, sill}
 *   rooms   {x, y, width, height, cls, parts}
 *   bounds  {minX, minY, maxX, maxY, width, height}
 */
export function toFloorPlanVectors(plan) {
  const segments = plan?.segments || [];
  const walls = [];
  const doors = [];
  const windows = [];

  segments.forEach((s, wallIndex) => {
    const horizontal = s.axis === 'h';
    walls.push({
      // y here is the plan's second axis — the editor's Y, the scene's Z.
      start: horizontal ? { x: s.from, y: s.at } : { x: s.at, y: s.from },
      end:   horizontal ? { x: s.to,   y: s.at } : { x: s.at, y: s.to },
      thickness: WALL_THICKNESS,
      axis: s.axis,
    });

    for (const o of s.openings || []) {
      const mid = (o.from + o.to) / 2;
      const marker = {
        center: horizontal ? { x: mid, y: s.at } : { x: s.at, y: mid },
        width: Math.abs(o.to - o.from),
        axis: s.axis,
        wallIndex,
      };
      if (o.type === 'window') windows.push({ ...marker, sill: WINDOW_SILL });
      else doors.push(marker);
    }
  });

  const rooms = (plan?.rooms || []).map(r => ({
    x: Math.min(r.x0, r.x1),
    y: Math.min(r.z0, r.z1),
    width: Math.abs(r.x1 - r.x0),
    height: Math.abs(r.z1 - r.z0),
    cls: r.cls || null,
    parts: r.parts || null,
  }));

  const xs = walls.flatMap(w => [w.start.x, w.end.x]).concat(rooms.flatMap(r => [r.x, r.x + r.width]));
  const ys = walls.flatMap(w => [w.start.y, w.end.y]).concat(rooms.flatMap(r => [r.y, r.y + r.height]));
  const bounds = xs.length
    ? {
      minX: Math.min(...xs), minY: Math.min(...ys),
      maxX: Math.max(...xs), maxY: Math.max(...ys),
      width: Math.max(...xs) - Math.min(...xs),
      height: Math.max(...ys) - Math.min(...ys),
    }
    : { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };

  return { walls, doors, windows, rooms, bounds };
}
